/**
 * Payoff timeline for the Credit Cards view.
 *
 * One row per card on a SHARED time axis, with a plot point at that card's
 * payoff date. A single-axis scatter was the obvious first shape, but real
 * schedules cluster — an Apr 5 and an Apr 9 payoff collide, and the labels
 * become unreadable exactly where the plan is most interesting. Giving each
 * card its own lane keeps every point labelled and, as a bonus, makes the
 * payoff ORDER legible at a glance.
 */

import React, { useMemo } from 'react';
import { CreditCard, PayoffPlan } from '../../types';
import { addMonths, daysBetween, startOfDay, today as todayFn, toLocalDay } from '../../utils/finance/dates';

interface PayoffTimelineProps {
  creditCards: CreditCard[];
  plans: PayoffPlan[];
}

interface Milestone {
  card: CreditCard;
  /** null = no active covering plan, or a plan that never reaches zero. */
  payoffDate: Date | null;
  /** Set when a plan exists but does not pay the card off. */
  note: string | null;
  isStale: boolean;
}

const ZERO = 0.05;

/** The date a card's balance actually reaches zero under its active plan. */
function payoffMilestone(card: CreditCard, plans: PayoffPlan[]): Milestone {
  const active = plans.filter(p => p.creditCardId === card.id && p.status === 'ACTIVE');
  const covering = active.find(p => p.mode !== 'LUMP_SUM');
  const isStale = active.some(p => p.isStale);

  if (!covering) {
    return {
      card,
      payoffDate: null,
      note: active.length > 0 ? 'One-time payment only' : 'No plan',
      isStale,
    };
  }
  const last = covering.payments[covering.payments.length - 1];
  if (!last || last.closingBalance > ZERO) {
    return { card, payoffDate: null, note: 'Never pays off', isStale };
  }
  return { card, payoffDate: toLocalDay(last.dueDate), note: null, isStale };
}

function fmt(d: Date, withYear: boolean): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

export const PayoffTimeline: React.FC<PayoffTimelineProps> = ({ creditCards, plans }) => {
  const model = useMemo(() => {
    const start = todayFn();
    const milestones = creditCards.map(c => payoffMilestone(c, plans));
    const dated = milestones.filter(m => m.payoffDate) as (Milestone & { payoffDate: Date })[];
    if (dated.length === 0) return null;

    dated.sort((a, b) => a.payoffDate.getTime() - b.payoffDate.getTime());
    const undated = milestones.filter(m => !m.payoffDate);

    // Pad the axis to the end of the final month so the last plot point isn't
    // pinned against the right edge with nowhere to put its label.
    const lastDate = dated[dated.length - 1].payoffDate;
    const axisEnd = startOfDay(new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 1));
    const span = Math.max(1, daysBetween(start, axisEnd));
    const spanMonths = span / 30.44;

    // Thin the ticks out as the horizon grows, or the labels collide.
    const step = spanMonths <= 9 ? 1 : spanMonths <= 20 ? 2 : 3;
    const ticks: { key: string; label: string; year: string | null; pct: number }[] = [];
    let cursor = startOfDay(new Date(start.getFullYear(), start.getMonth(), 1));
    for (let i = 0; i < 200; i += 1) {
      if (cursor > axisEnd) break;
      const offset = daysBetween(start, cursor);
      if (offset >= 0) {
        const isJan = cursor.getMonth() === 0;
        ticks.push({
          key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
          label: cursor.toLocaleDateString('en-US', { month: 'short' }),
          // Only January carries a year, which is enough to disambiguate a
          // horizon that crosses into the next one.
          year: isJan ? `'${String(cursor.getFullYear()).slice(2)}` : null,
          pct: (offset / span) * 100,
        });
      }
      cursor = addMonths(cursor, step);
    }

    return { start, span, ticks, dated, undated, lastDate };
  }, [creditCards, plans]);

  if (!model) return null;

  const { start, span, ticks, dated, undated, lastDate } = model;
  const pctFor = (d: Date) => Math.min(100, Math.max(0, (daysBetween(start, d) / span) * 100));

  return (
    <div className="mb-11">
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <span className="text-[12px] font-semibold tracking-[0.06em] uppercase text-ink-muted">
          Payoff Timeline
        </span>
        <p className="text-[11.5px] text-ink-muted">
          Debt-free{' '}
          <span className="text-accent font-semibold">{fmt(lastDate, true)}</span>
        </p>
      </div>

      {/* Month axis. Shares the grid with the rows below so ticks line up. */}
      <div className="flex items-end gap-2 sm:gap-3">
        <div className="w-24 shrink-0 sm:w-36" />
        <div className="relative h-4 flex-1">
          {ticks.map(t => (
            <span
              key={t.key}
              className="absolute top-0 -translate-x-1/2 whitespace-nowrap text-[10.5px] font-medium text-ink-muted"
              style={{ left: `${t.pct}%` }}
            >
              {t.label}
              {t.year && <span className="ml-0.5 text-ink-muted">{t.year}</span>}
            </span>
          ))}
        </div>
        <div className="w-14 shrink-0 sm:w-20" />
      </div>

      <div className="space-y-1.5">
        {dated.map(m => {
          const pct = pctFor(m.payoffDate);
          return (
            <div key={m.card.id} className="flex items-center gap-2 sm:gap-3">
              <div className="w-24 shrink-0 truncate text-[13.5px] text-ink sm:w-36">
                {m.card.name}
              </div>

              <div className="relative h-7 flex-1">
                {/* Gridlines, so a dot can be read against the axis. */}
                {ticks.map(t => (
                  <span
                    key={t.key}
                    className="absolute top-0 h-full w-px bg-rule"
                    style={{ left: `${t.pct}%` }}
                  />
                ))}
                <span className="absolute top-1/2 h-[3px] w-full -translate-y-1/2 rounded-full bg-rule" />
                <span
                  className={`absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full ${
                    m.isStale ? 'bg-rule-strong' : 'bg-accent'
                  }`}
                  style={{ width: `${pct}%` }}
                />
                <span
                  className={`absolute top-1/2 h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full ${
                    m.isStale ? 'bg-surface border border-rule-strong' : 'bg-accent'
                  }`}
                  style={{ left: `${pct}%` }}
                  title={`${m.card.name} — paid off ${fmt(m.payoffDate, true)}`}
                />
              </div>

              <div className="w-14 shrink-0 text-right text-[11px] font-medium text-ink-muted sm:w-20">
                {fmt(m.payoffDate, false)}
              </div>
            </div>
          );
        })}
      </div>

      {undated.length > 0 && (
        <div className="mt-5 border-t border-rule pt-4">
          <p className="mb-2.5 text-[11px] font-semibold text-ink-muted">
            Not on a payoff schedule
          </p>
          <div className="flex flex-wrap gap-1.5">
            {undated.map(m => (
              <span
                key={m.card.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-rule-strong px-2.5 py-1 text-[11px] font-medium text-ink-muted"
              >
                {m.card.name}
                <span className="text-ink-muted">· {m.note}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
